import axios from 'axios';
import { environment } from '../../environments/environment';
import { downloadText } from '../Components/Benchmark/supplied/csvExport';

const getBaseUrl = () => `${environment.controlplaneUrl}/api/v1/benchmarks`;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

/**
 * Whether the run actually recorded a figure.
 *
 * The API returns null for anything it did not measure rather than a plausible
 * default, and pairs it with one of these so the UI can say "not computed for
 * this run" instead of leaving a suspicious blank.
 */
export type Availability = 'computed' | 'not_available';

/** A tier code as stored. Display names live in BenchmarkVocabulary. */
export type TierCode = 'P5' | 'P4' | 'P3' | 'P2';

/**
 * Where a run's data came from: the institution's own mined alert history, or a
 * sample of alerts a user supplied.
 */
export type DataSource = 'mined' | 'user_supplied';

/** How the loader identified a supplied column. `llm` means the model did. */
export type ColumnMethod = 'exact' | 'alias' | 'human' | 'llm' | string;

export interface ColumnMappingEntry {
  source: string;
  target: string;
  method: ColumnMethod | null;
}

/** The KYC a supplied run read, and how it was dated against the alerts. */
export interface KycSummary {
  sourceName: string | null;
  rowsSupplied: number | null;
  matched: number | null;
  suppliedCustomers: number | null;
  customerMasterCustomers: number | null;
  pointInTime: number | null;
  postAlert: number | null;
  undated: number | null;
}

/** Present on every run; a stock run carries only `dataSource: 'mined'`. */
export interface RunSource {
  dataSource: DataSource;
  sourceName?: string | null;
  rowsSupplied?: number | null;
  contentFingerprint?: string | null;
  columnMapping?: ColumnMappingEntry[];
  unmappedColumns?: string[];
  unmappedDispositions?: string[];
  kyc?: KycSummary | null;
  warnings?: string[];
}

/**
 * A metric family the run refused to compute, or computed with a caveat.
 *
 * An entry with `qualifies` annotates the families it names. Any other entry
 * withholds its own family, plus every family in `suppresses`.
 */
export interface WithheldMetric {
  metric_family: string;
  reason: string;
  suppresses?: string[] | null;
  qualifies?: string[] | null;
}

/**
 * What a run measured, and so which endpoints describe it: a priority run has
 * the tier views, an investigation run the dimension views. The API refuses a
 * tier view for an investigation run (409) rather than answering it with zeros.
 */
export type BenchmarkType = 'priority_assignment' | 'case_investigation';

export interface BenchmarkRun {
  runKey: number;
  runId: string;
  orgCode: string;
  projectId: string;
  flowTemplateCode: string;
  /** The flow code with any publish stamp removed. */
  flowFamily?: string | null;
  /** Absent on an API predating investigation benchmarks, which means a priority run. */
  benchmarkType?: BenchmarkType;
  /** What an investigation run compared. Null for a priority run. */
  comparisonSpec?: ComparisonSpec | null;
  /** The agents the caller selected for an investigation run. */
  agentSelection?: AgentSelection | null;
  dataSource?: DataSource;
  isCurrent?: boolean | null;
  sessionId?: string | null;
  comparisonMode: string;
  populationFrame: string;
  datasetVersionId?: number;
  predictionCount: number;
  startedAt: string;
  completedAt: string;
  datasetProvenance?: Record<string, unknown> | null;
  scorerConfig?: Record<string, unknown> | null;
  source?: RunSource;
  withheldMetrics?: WithheldMetric[];
  warnings?: string[];
}

/** One row of the run picker. */
export interface BenchmarkRunSummary {
  runKey: number;
  runId: string;
  orgCode: string;
  projectId: string;
  flowTemplateCode: string;
  flowFamily: string;
  /** What the run measured; absent from an API predating investigation benchmarks. */
  benchmarkType?: BenchmarkType;
  /** Where an investigation run's cases entered: 'context' (ENRICH) or 'edd'. */
  stage?: string | null;
  agentSelection?: AgentSelection | null;
  dataSource: DataSource;
  isCurrent: boolean;
  sessionId: string | null;
  comparisonMode: string;
  datasetVersionId: number | null;
  predictionCount: number | null;
  sourceName: string | null;
  rowsSupplied: number | null;
  /** Who submitted the sample. Null on runs made before submitters were recorded. */
  submittedBy?: string | null;
  kyc: KycSummary | null;
  withheldCount: number;
  startedAt: string;
  completedAt: string | null;
}

export interface RunListResponse {
  runs: BenchmarkRunSummary[];
  pagination: { limit: number; offset: number; total: number };
}

export interface RunListQuery {
  source?: DataSource | 'all';
  /** One family, or several. */
  flow_family?: string | string[];
  project_id?: string;
  session_id?: string;
  /** One submitter's runs; `me` is the caller. */
  submitted_by?: string;
  limit?: number;
  offset?: number;
}

export interface SuppliedAlertRow {
  alertRef: string;
  customerRef: string | null;
  scenarioId: string | null;
  configEpoch: string | null;
  openedAt: string | null;
  priority: string | null;
  prioritySource: string | null;
  disposition: string | null;
  dispositionRaw: string | null;
  sarFiled: boolean | null;
  alertScore: number | null;
  features: Record<string, unknown>;
  contextFeatures: Record<string, unknown>;
}

export interface SuppliedKycRow {
  customerRef: string;
  kycAsOf: string | null;
  profile: Record<string, unknown>;
}

export interface RunSampleResponse {
  runKey: number;
  datasetVersionId: number;
  alerts: SuppliedAlertRow[];
  kyc: SuppliedKycRow[];
  pagination: { limit: number; offset: number; total: number };
}

/**
 * How much evidence stands behind a figure. On a small supplied sample this
 * matters more than the value: a ROC AUC of 1.0 over 8 cases proves very little.
 */
export interface KpiEvidence {
  sampleSize?: number | null;
  caseCount?: number | null;
  confidence?: 'low' | 'medium' | 'high' | string | null;
}

export interface ExecutiveSummaryResponse {
  runKey: number;
  totals: {
    alerts: number;
    truePositives: number;
    sarsFiled: number;
  };
  /** For the count-derived KPIs; metric-derived ones carry their own. */
  evidence?: KpiEvidence;
  kpis: {
    /** What stopping at the two lowest tiers would save, and what it would cost. */
    workloadReduction: {
      suppressedTiers: TierCode[];
      alertsSuppressed: number;
      workloadSavedFraction: number | null;
      trueRiskRetainedFraction: number | null;
      sarsRetainedFraction: number | null;
      confirmedRisksGivenUp: number;
      sarsGivenUp: number;
    };
    /** Agent's urgent queue vs the incumbent rules' own urgent tier. */
    urgentQueueHitRate: {
      tier: TierCode;
      alertsInTier: number;
      confirmedInTier: number;
      hitRate: number | null;
      incumbentHitRate: number | null;
      incumbentAlertsInTier: number;
      lift: number | null;
      /** Share of the whole caseload that is a confirmed risk. */
      unrankedHitRate: number | null;
    };
    topQueueHitRate: {
      queueFraction: number;
      hitRate: number | null;
      delta: number | null;
      incumbentHitRate: number | null;
      evidence?: KpiEvidence | null;
    };
    sarConcentration: {
      tier: TierCode;
      sarsInTier: number;
      totalSars: number;
      rate: number | null;
    };
    /** Model-validation figures. Shown on the Evidence tab only. */
    rankingQuality: {
      rocAuc: number | null;
      baselineRocAuc: number | null;
      delta: number | null;
      confidenceInterval95: [number, number] | null;
      bootstrapResamples: number | null;
      statisticallySignificant: boolean | null;
      evidence?: KpiEvidence | null;
    };
  };
  rescued: {
    fromTiers: TierCode[];
    toTiers: TierCode[];
    total: number;
    sarFilings: number;
  };
  availability: {
    topQueueHitRate: Availability;
    rankingQuality: Availability;
    confidenceInterval: Availability;
  };
}

export interface ReductionCurvePoint {
  reviewFraction: number;
  workloadSavedFraction: number;
  /** Null when the run recorded no curve for that outcome. */
  truePositiveRecall: number | null;
  sarRecall: number | null;
}

export interface ReviewPolicy {
  activeTiers: TierCode[];
  alertsReviewed: number;
  reviewFraction: number | null;
  workloadSavedFraction: number | null;
  truePositiveRecall: number | null;
  sarRecall: number | null;
  hitRate: number | null;
}

export interface WorkloadSimulatorResponse {
  runKey: number;
  evidence?: KpiEvidence;
  totalAlertCount: number;
  totalTruePositives: number;
  totalSARsFiled: number;
  reductionCurve: ReductionCurvePoint[];
  reviewPolicies: ReviewPolicy[];
  availability: {
    truePositiveCurve: Availability;
    sarCurve: Availability;
  };
}

export interface AgentTierSummary {
  tier: TierCode;
  totalCount: number;
  percentOfTotal: number | null;
  truePositives: number;
  falsePositives: number;
  sarsFiled: number;
  hitRate: number | null;
  sarRate: number | null;
}

export interface TierMigrationLink {
  fromIncumbent: string;
  toAgent: string;
  count: number;
  truePositives: number;
  sars: number;
}

/** A metric as the scorer recorded it, with the evidence behind it. */
export interface EvidencedMetric {
  value: number;
  sampleSize: number | null;
  confidence: KpiEvidence['confidence'];
}

export interface DispositionMatrixResponse {
  runKey: number;
  totalAlertCount: number;
  agentTiers: AgentTierSummary[];
  tierMigrationFlow: TierMigrationLink[];
  /** How often the platform's tier matched the institution's. Null where not recorded. */
  concordance?: {
    exactAgreement: EvidencedMetric | null;
    withinOneTier: EvidencedMetric | null;
  };
}

/* ------------------------------------------------------------------ *
 * A supplied run's results, alert by alert
 * ------------------------------------------------------------------ */

/**
 * Where a submission stands. `not_scored` covers a preview, a refusal and a
 * failed run; `reason` is the loader's refusal verbatim, null for a preview or
 * when nothing recorded one.
 */
export type RunReferenceStatus =
  | { referenceId: string; status: 'complete'; runKey: number }
  | { referenceId: string; status: 'running'; flowStatus?: string | null }
  | {
      referenceId: string;
      status: 'not_scored';
      flowStatus?: string | null;
      reason: string | null;
      preview: boolean;
      failedStep?: string | null;
    };

/** How an alert's own tier compares with the tier the platform gave its case. */
export type AgreementKind = 'agree' | 'platform_higher' | 'platform_lower' | 'no_comparable_tier' | 'not_scored';

/** One supplied alert, joined to its case's prediction. */
export interface SuppliedResultRow {
  alertRef: string;
  customerRef: string | null;
  /** The case the alert was scored in: its customer, or the alert itself without one. */
  caseRef: string;
  /** Alerts sharing this case, and so this platform tier. */
  alertsInCase: number;
  scenarioId: string | null;
  configEpoch: string | null;
  openedAt: string | null;
  /** The tier as the user's file wrote it. */
  institutionTierRaw: string | null;
  /** That tier on the platform's scale; null when it has no approved mapping. */
  institutionTier: string | null;
  prioritySource: string | null;
  disposition: string | null;
  dispositionRaw: string | null;
  sarFiled: boolean | null;
  alertScore: number | null;
  platformTier: string | null;
  platformScore: number | null;
  hardOverride: string | boolean | null;
  scoreBreakdown: Record<string, number | string | null>;
  agreement: AgreementKind;
}

export interface RunResultsSummary {
  alertCount: number;
  caseCount: number;
  scoredCaseCount: number;
  byAgreement: Record<AgreementKind, number>;
  scenarios: string[];
  outcomes: string[];
}

export interface RunResultsResponse {
  runKey: number;
  alerts: SuppliedResultRow[];
  pagination: { limit: number; offset: number; total: number };
  /** Counts over the whole result set, whatever the filters. */
  summary: RunResultsSummary;
}

export interface RunResultsQuery {
  agreement?: AgreementKind;
  outcome?: string;
  platform_tier?: string;
  scenario_id?: string;
  sar_filed?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Whether a layer or agent found evidence. `not_found` and `skipped` mean the
 * signal was absent and scored zero — no evidence, not negative evidence.
 */
export type EvidenceStatus = 'found' | 'context_derived' | 'kyc_derived' | 'not_found' | 'skipped' | 'error' | string;

/** Per layer (`layer1_base_signals` …) and, under `agents`, per investigating agent. */
export type ComponentStatus = Record<string, EvidenceStatus | Record<string, EvidenceStatus> | undefined> & {
  agents?: Record<string, EvidenceStatus>;
};

/** Why one case got its tier. */
export interface CaseDetailResponse {
  runKey: number;
  caseRef: string;
  customerRef: string | null;
  platformTier: string | null;
  platformScore: number | null;
  hardOverride: string | boolean | null;
  /** The tier the score alone would have given, before any hard override. */
  platformTierWithoutOverride: string | null;
  scoreBreakdown: Record<string, number | string | null>;
  componentStatus: ComponentStatus;
  featureSnapshot: Record<string, unknown>;
  /** The customer's KYC risk context: recorded, never scored. */
  subjectContext: Record<string, unknown>;
  baselineScore: number | null;
  baselineFeatures: Record<string, unknown>;
  institutionTier: string | null;
  institutionTierRaw: string | null;
  outcomeClass: string | null;
  outcomeLabels: Record<string, unknown>;
  asOf: string | null;
  /** Null when the run was given no KYC. */
  kycSource: 'supplied' | 'customer_master' | null;
  kycAsOf: string | null;
  alerts: SuppliedResultRow[];
  benchmarkType?: BenchmarkType;
  /** An investigation run's comparisons for this case, one per dimension. */
  comparisons?: CaseComparison[];
}

/* ------------------------------------------------------------------ *
 * Investigation benchmarks: comparisons by dimension
 *
 * An investigation run compares, case by case, each of the stage's decisions
 * (refer to EDD, SAR), each selected agent's verdict, each risk factor and each
 * typology with what the institution recorded. The run's manifest says what
 * each dimension is, so nothing here knows what a referral or an agent is.
 * ------------------------------------------------------------------ */

export type DimensionType = 'decision' | 'agent' | 'factor' | 'typology';

/**
 * How one case's two sides compare on one dimension: the first four for a yes/no
 * dimension; agree, adjacent (one class apart, ordinal only) and disagree for one
 * with classes, such as an EDD rating or disposition.
 */
export type ComparisonAgreement =
  | 'both_flagged' | 'both_clear' | 'platform_only' | 'institution_only'
  | 'agree' | 'adjacent' | 'disagree' | 'not_comparable';

export interface AgentSelection {
  mode: 'exact' | 'restrict' | string;
  agents: string[];
  unknown?: string[];
}

/** One entry of a run's manifest, as stored (snake_case). */
export interface ComparisonSpecDimension {
  dimension_key: string;
  dimension_type: DimensionType;
  label?: string;
  value_kind?: string;
  classes?: string[];
  positive_classes?: string[];
  class_labels?: Record<string, string>;
  producer?: string | string[] | null;
  producer_label?: string | null;
  drives_decision?: boolean;
}

export interface ComparisonSpec {
  stage: string | null;
  /** The decision the run is headlined by (EDD: sar_referral). */
  headline?: string | null;
  dimensions: ComparisonSpecDimension[];
  agent_selection?: AgentSelection | null;
  unselected_agents?: string[];
  /** Case counts of the cohorts the metrics were also written for. */
  cohorts?: Record<string, number>;
}

/**
 * An outcome the run's metrics are associated with (a SAR filed, a confirmed
 * suspicion). circularFor names the side whose association is inflated by
 * construction: the institution's analysts recorded its factors and also decided it.
 */
export interface RunOutcome {
  key: string;
  label: string;
  circularFor: 'institution' | 'platform' | null;
}

/** One figure, with the evidence behind it. */
export interface DimensionMetricValue {
  value: number | null;
  sampleSize: number | null;
  confidence: 'low' | 'medium' | 'high' | string | null;
  indicative: boolean;
  counts?: Partial<Record<ComparisonAgreement, number>>;
  statuses?: Record<string, number>;
  /** A 95% interval: an odds ratio's, or a platform-minus-institution difference's. */
  ci?: [number, number];
  /** Whether that difference's interval excludes zero. */
  separable?: boolean;
  /** An ordinal or nominal decision's predicted × actual counts. */
  confusion?: Record<string, Record<string, number>>;
}

/**
 * Metric name → label → figure. A figure without a label is under `all`; one
 * per outcome label under that label (`tp`, `sar`).
 */
export type DimensionMetrics = Record<string, Record<string, DimensionMetricValue>>;

/** The manifest entry, in the API's casing. */
export interface DimensionManifest {
  dimensionKey: string;
  dimensionType: DimensionType;
  label: string;
  /**
   * binary | score | count — compared at a flag threshold — or ordinal (classes in
   * order, lowest first: an EDD rating) or nominal (unordered: a disposition).
   */
  valueKind: string;
  /** Lowest or negative class first. */
  classes: string[];
  /** The classes that read as "flagged". Absent from older runs: the last class. */
  positiveClasses?: string[];
  /** A label per class code, from the run. */
  classLabels?: Record<string, string>;
  /** The agent, node or (for a typology) agents that produce the platform's side. */
  producer: string | string[] | null;
  /** The producing agent's name, from the run. */
  producerLabel?: string | null;
  typologies: string[];
  factors: string[];
  flagAt: number | null;
  eddTrigger: string | null;
  /** False for a factor the stage's decisions never read: compared, but unable to move them. */
  drivesDecision?: boolean;
}

export interface ComparisonDimension extends DimensionManifest {
  subjects: number;
  comparable: number;
  agreementCounts: Record<ComparisonAgreement, number>;
  statusCounts: Record<string, number>;
  metrics: DimensionMetrics;
}

export interface DimensionsResponse {
  runKey: number;
  benchmarkType: BenchmarkType;
  availability: Availability;
  /** Present when nothing was compared dimension-wise. */
  reason?: string;
  stage?: string | null;
  headline?: string | null;
  outcomes?: RunOutcome[];
  cohorts?: Record<string, number>;
  agentSelection?: AgentSelection | null;
  unselectedAgents?: string[];
  withheldMetrics?: WithheldMetric[];
  dimensions?: ComparisonDimension[];
  /**
   * Each detection scenario the run's cases came from, with the typologies its approved
   * mapping named when the run was scored. Null on runs scored before mappings existed.
   */
  scenarios?: Record<string, RunScenario> | null;
}

/** One of the institution's detection scenarios, as an investigation run recorded it. */
export interface RunScenario {
  /** The rule's name, when the mapping recorded one. */
  label: string | null;
  /** Empty when the scenario had no approved typology at scoring time. */
  typologies: { code: string; label: string }[];
  cases: number;
}

/** One cell of a dimension's institution × platform grid. */
export interface DimensionCell {
  institution: string;
  platform: string;
  agreement: 'agree' | 'adjacent' | 'disagree';
  /** Off the diagonal: which side called the case higher. */
  direction?: 'platform_higher' | 'institution_higher' | null;
  count: number;
  /** Per outcome key, the cases in this cell that met it. */
  outcomes?: Record<string, number>;
  truePositives: number;
  sarsFiled: number;
}

export interface DimensionDetailResponse {
  runKey: number;
  availability: Availability;
  dimension: DimensionManifest;
  outcomes?: RunOutcome[];
  comparable: number;
  cells: DimensionCell[];
  /** Why the cases outside the grid could not be compared, by status. */
  notComparable: { status: string; count: number; institutionMissing: number; platformMissing: number }[];
  metrics: DimensionMetrics;
}

export interface DimensionSubject {
  caseRef: string;
  customerRef: string | null;
  platformValue: string | null;
  platformScore: number | null;
  institutionValue: string | null;
  institutionScore: number | null;
  status: string | null;
  agreement: ComparisonAgreement;
  detail: Record<string, unknown>;
  scenarioId: string | null;
  /** Every scenario among the case's alerts; the one alert's scenario on older runs. */
  scenarioIds?: string[];
  configEpoch: string | null;
  outcomeClass: string | null;
  outcomeLabels?: Record<string, unknown>;
  sarFiled: boolean | null;
}

export interface DimensionSubjectsResponse {
  runKey: number;
  dimensionKey: string;
  subjects: DimensionSubject[];
  pagination: { limit: number; offset: number; total: number };
}

export interface DimensionSubjectsQuery {
  agreement?: ComparisonAgreement;
  /** Either side's class, e.g. REFER. */
  platform?: string;
  institution?: string;
  status?: string;
  outcome?: string;
  limit?: number;
  offset?: number;
}

/** One case on one dimension, as the case drawer shows it. */
export interface CaseComparison {
  dimensionKey: string;
  dimensionType: DimensionType;
  producer: string | string[] | null;
  platformValue: string | null;
  platformScore: number | null;
  institutionValue: string | null;
  institutionScore: number | null;
  status: string | null;
  agreement: ComparisonAgreement;
  detail: Record<string, unknown>;
}

/** One side's association with an outcome, on one dimension. */
export interface AssociationSide {
  /** The outcome rate where this side flagged, and where it did not. */
  rateFlagged: number | null;
  rateUnflagged: number | null;
  lift: number | null;
  oddsRatio: number | null;
  oddsRatioCi: [number, number] | null;
  /** The correlation of the flag with the outcome, -1 to 1: comparable across factors. */
  phi: number | null;
  auc: number | null;
  sampleSize: number | null;
}

export interface OutcomeAssociationRow extends DimensionManifest {
  platform: AssociationSide;
  institution: AssociationSide;
  /** Platform phi minus institution phi, paired on the same cases, with its interval. */
  delta: { phi: number | null; ci: [number, number] | null; separable: boolean; sampleSize: number | null };
  /** The outcome rate where only the platform flagged: what the institution's record missed. */
  platformOnlyOutcomeRate: number | null;
  platformOnlyCases: number | null;
  indicative: boolean;
}

export interface OutcomeAssociationResponse {
  runKey: number;
  availability: Availability;
  reason?: string;
  stage?: string | null;
  outcome?: RunOutcome;
  outcomes?: RunOutcome[];
  cohort?: string | null;
  cohorts?: Record<string, number>;
  dimensions?: OutcomeAssociationRow[];
}

export interface OutcomeAssociationQuery {
  outcome?: string;
  dimensionType?: DimensionType;
  producer?: string;
  cohort?: string;
}

/** One agent a flow can run, as the launch picker lists it. */
export interface CatalogAgent {
  agent: string;
  label: string;
  nodes: string[];
  /** False when the catalogue knows the agent but this flow does not dispatch it. */
  inFlow: boolean;
  appliesTo: 'any' | 'organisation' | string;
  /** Reads today's external sources (watchlists, news, registries, websites). */
  external: boolean;
  /** Kept on a live review whatever the selection (an EDD check). */
  mandatory: boolean;
  mandatoryFor: string[];
  factors: { factorCode: string; label: string; kind: string; typologies: string[]; drivesDecision: boolean }[];
}

export interface AgentCatalog {
  flowCode: string;
  supervisedFlow: string;
  stage: 'context' | 'edd' | string;
  modes: string[];
  defaultMode: 'exact' | 'restrict';
  /** False for an EDD review: exact selection applies only to benchmark replays there. */
  exactHonouredLive: boolean;
  agents: CatalogAgent[];
  alwaysCompared: { agent: string; factors: CatalogAgent['factors'] }[];
}

/** Whether a run is described by the dimension views rather than the tier views. */
export const isInvestigationRun = (run: Pick<BenchmarkRun, 'benchmarkType'> | null | undefined): boolean =>
  run?.benchmarkType === 'case_investigation';

export interface TypologySummary {
  segmentCode: string;
  sampleSize: number;
  alertCount?: number;
  confirmedCount?: number;
  sarCount: number;
  /** Hit rate in the tiers the agent marked worth working. */
  elevatedHitRate?: number | null;
  agentRocAuc: number | null;
  baselineRocAuc: number | null;
  delta: number | null;
  top10Precision: number | null;
  liftAt10Pct: number | null;
}

export interface EpochStability {
  epoch: string;
  sampleSize: number;
  agentRocAucTP: number | null;
  agentRocAucSAR: number | null;
  baselineRocAucTP: number | null;
}

export interface TypologyPerformanceResponse {
  runKey: number;
  typologies: TypologySummary[];
  epochStability: EpochStability[];
  availability: {
    typologyMetrics: Availability;
    epochStability: Availability;
  };
}

export interface LayerContribution {
  layerKey: string;
  aucTP: number | null;
  aucSAR: number | null;
}

export interface RiskFeature {
  featureKey: string;
  aucTP: number | null;
  aucSAR: number | null;
  importanceRank: number;
}

export interface RiskDriversResponse {
  runKey: number;
  layerContributions: LayerContribution[];
  topRiskFeatures: RiskFeature[];
  availability: {
    layerContributions: Availability;
    topRiskFeatures: Availability;
  };
}

/**
 * A signal that pushed an alert up, as a code and a value.
 *
 * The API used to return finished sentences ("velocity burst exceeding 90th
 * percentile" — a threshold nothing had measured). Phrasing is the UI's job, so
 * these stay structured and BenchmarkVocabulary turns them into copy.
 */
export interface ElevatingSignal {
  code: string;
  value: number | boolean;
}

export interface ScoredAlert {
  alertId: string;
  customerRef: string;
  typology: string;
  configEpoch: string;
  agentTier: TierCode;
  agentScore: number | null;
  incumbentTier: TierCode;
  incumbentScore: number | null;
  outcomeClass: string;
  sarFiled: boolean;
  isRescued: boolean;
  elevatingSignals: ElevatingSignal[];
  scoreBreakdown: Record<string, number>;
  featureSnapshot: Record<string, any>;
}

export interface ScoredAlertsResponse {
  pagination: {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
  alerts: ScoredAlert[];
}

export interface RegulatoryDossierResponse {
  runKey: number;
  runId: string;
  orgCode: string;
  flowTemplateCode: string;
  generatedAt: string;
  governanceStandards: string;
  specifications: {
    targetPopulationFrame: string | null;
    comparisonMode: string | null;
    evaluationPeriods: string[];
    datasetVersionId: number | null;
    datasetProvenance: Record<string, unknown> | null;
    scorerFingerprint: string | null;
    scorerFingerprintAlgorithm: string | null;
  };
  statisticalValidation: {
    rocAucDelta: number | null;
    rocAucDelta95CI: [number, number] | null;
    bootstrapResamples: number | null;
    intervalExcludesZero: boolean | null;
  };
  availability: Record<string, Availability>;
}

export interface ScoredAlertsQuery {
  page?: number;
  page_size?: number;
  predicted_tier?: string;
  incumbent_tier?: string;
  filter_rescued_only?: boolean | string;
  outcome_class?: string;
  sar_only?: boolean | string;
  segment_key?: string;
}

/**
 * Turns an axios rejection into something a panel can show a person.
 *
 * The dashboard previously logged failures to the console and rendered its
 * loading skeleton forever, which reads as "still working" rather than "this
 * did not load" — so every caller here gets a message it can put on screen.
 */
export class BenchmarkRequestError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'BenchmarkRequestError';
    this.status = status;
  }
}

function toRequestError(error: any): BenchmarkRequestError {
  const status = error?.response?.status ?? null;
  if (status === 404) {
    return new BenchmarkRequestError('No results were found for this test run.', status);
  }
  if (status === 401 || status === 403) {
    return new BenchmarkRequestError('Your session has expired. Sign in again to view these results.', status);
  }
  const serverMessage = error?.response?.data?.error;
  return new BenchmarkRequestError(
    serverMessage || 'Could not load these results. Check your connection and try again.',
    status
  );
}

async function get<T>(path: string, params?: Record<string, any>): Promise<T> {
  try {
    const res = await axios.get(`${getBaseUrl()}${path}`, { headers: getHeaders(), params });
    return res.data?.data as T;
  } catch (error) {
    throw toRequestError(error);
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  try {
    const res = await axios.post(`${getBaseUrl()}${path}`, body, { headers: getHeaders() });
    return res.data?.data as T;
  } catch (error) {
    throw toRequestError(error);
  }
}

/* ------------------------------------------------------------------ *
 * Supplied samples
 * ------------------------------------------------------------------ */

export type MappingRelation = 'case_history_alert' | 'customer_master';

/** What an org needs before a supplied sample can be benchmarked. */
export interface SamplePrerequisites {
  /** Every run is refused without an approved mapping of the org's tiers onto ours. */
  tierMapping: { approved: boolean; tiers: Record<string, string | null> };
  /** Columns the model identified in an earlier file that nobody has confirmed. */
  pendingMappings: { alerts: number; kyc: number };
}

export interface PendingColumn {
  source: string;
  targetColumn: string | null;
  confidence: number | null;
  proposedBy: string | null;
  proposedAt: string | null;
  /** A required field is never taken on the model's word alone. */
  required: boolean;
}

export interface FieldMappingsResponse {
  relation: MappingRelation;
  status: string | null;
  columns: Record<string, { targetColumn?: string; method?: string; [key: string]: unknown }>;
  pending: PendingColumn[];
  canonical: string[];
  required: string[];
}

export interface ConfirmMappingsResponse {
  relation: MappingRelation;
  confirmed: { source: string; targetColumn: string }[];
  rejected: string[];
  skipped: { source: string; reason: string; targetColumn: string }[];
}

/** What the loader reports when a sample is previewed. Fields are as it writes them. */
export interface SupplyPreview {
  dataset_version_id?: number | null;
  comparison_mode?: string | null;
  sample_spec?: {
    dataset_source?: {
      source_name?: string;
      rows_supplied?: number;
      column_mapping?: Record<string, string>;
      column_methods?: Record<string, string>;
      unmapped_columns?: string[];
      unmapped_dispositions?: string[];
      kyc?: Record<string, unknown> | null;
    };
    [key: string]: unknown;
  };
  coverage?: Record<string, any>;
  class_mapping?: Record<string, string>;
  labels_available?: Record<string, { available?: boolean; count?: number; [key: string]: unknown }>;
  warnings?: string[];
  rows?: Array<{
    alert_ref?: string;
    customer_ref?: string;
    scenario_id?: string;
    config_epoch?: string;
    priority?: string | null;
    priority_mapped?: string | null;
    disposition?: string | null;
    sar_filed?: boolean | null;
    [key: string]: unknown;
  }>;
  rows_listed?: number;
  sessions?: number;
}

/** The body the supplied-sample flow is run with, previewed or not. */
export interface SuppliedSampleData {
  projectId: string;
  alertRecords: Record<string, unknown>[];
  customerRecords?: Record<string, unknown>[];
  /** The file names, so the run can say what it scored. */
  sourceName?: string;
  kycSourceName?: string;
  previewOnly?: boolean;
  previewRows?: number;
  /** The agents the replay runs, and so whose risk factors are compared; absent, all. */
  agentSelection?: AgentSelection;
}

export interface SuppliedSampleSubmission {
  /** The run's session: follow its context stream, and find its benchmark run, by this. */
  referenceId: string | null;
  interactionId: string;
  async: boolean;
  status: string | null;
}

export interface ProjectCurrentRunsResponse {
  projectId: string;
  /** One run per benchmarked flow. Flows with no run are simply absent. */
  runs: BenchmarkRun[];
}

export const benchmarkService = {
  /**
   * The run a flow's results currently point at.
   *
   * Returns null for "this flow has not been benchmarked yet", which is an
   * ordinary state on the flow editor, and throws for anything else.
   */
  async getCurrentRun({ flowFamily, flowCode, projectId }: {
    /** The flow code with its publish stamp removed; every build of the flow matches. */
    flowFamily?: string;
    /** One exact code. Overrides the family. */
    flowCode?: string;
    projectId?: string;
  } = {}): Promise<BenchmarkRun | null> {
    const params: Record<string, string> = {};
    if (flowFamily) params.flow_family = flowFamily;
    if (flowCode) params.flow_code = flowCode;
    if (projectId) params.project_id = projectId;

    try {
      return await get<BenchmarkRun>('/current', params);
    } catch (error) {
      if (error instanceof BenchmarkRequestError && error.status === 404) return null;
      throw error;
    }
  },

  /**
   * The current run for every benchmarked flow in a project, in one request.
   *
   * The project page draws a card per workflow and wants to mark the ones that
   * have been backtested. Calling getCurrentRun per card would be a request per
   * card to discover that most of them have no run at all.
   *
   * An empty list means the project has never been backtested, which is an
   * ordinary state and not an error.
   */
  async getProjectCurrentRuns(projectId: string): Promise<BenchmarkRun[]> {
    if (!projectId) return [];
    const data = await get<ProjectCurrentRunsResponse>('/current-runs', { project_id: projectId });
    return data?.runs ?? [];
  },

  /**
   * One run by key.
   *
   * The dashboard needs this for deep links: it previously loaded the *current*
   * run and overwrote its key with the one in the URL, so an older run's charts
   * appeared under the current run's identity and timestamps.
   */
  getRun(runKey: number) {
    return get<BenchmarkRun>(`/runs/${runKey}`);
  },

  /**
   * Runs for the caller's org, newest first.
   *
   * Supplied samples are picked from this list rather than shown as one current
   * run: each scores a different sample, so the latest does not replace the
   * earlier ones.
   */
  listRuns(query: RunListQuery = {}) {
    const { flow_family, ...rest } = query;
    const params: Record<string, any> = { ...rest };
    const families = Array.isArray(flow_family) ? flow_family.filter(Boolean) : flow_family;
    if (Array.isArray(families) ? families.length : families) {
      params.flow_family = Array.isArray(families) ? families.join(',') : families;
    }
    return get<RunListResponse>('/runs', params);
  },

  /**
   * Where a submission stands, from the referenceId it was submitted with:
   * scored (with its run key), still running, or finished without a run.
   */
  getRunByReference(referenceId: string) {
    return get<RunReferenceStatus>(`/runs/by-reference/${encodeURIComponent(referenceId)}`);
  },

  /** A supplied run's results, one row per supplied alert, with counts over all of them. */
  getRunResults(runKey: number, query: RunResultsQuery = {}) {
    return get<RunResultsResponse>(`/runs/${runKey}/results`, query);
  },

  /**
   * Downloads every result matching the filters as CSV. The server builds the
   * file, so the export holds every row rather than the page on screen.
   */
  async downloadRunResultsCsv(runKey: number, query: Omit<RunResultsQuery, 'limit' | 'offset'> = {}): Promise<void> {
    let text: string;
    try {
      const res = await axios.get(`${getBaseUrl()}/runs/${runKey}/results`, {
        headers: getHeaders(),
        params: { ...query, format: 'csv' },
        responseType: 'text'
      });
      text = String(res.data ?? '');
    } catch (error) {
      throw toRequestError(error);
    }
    downloadText(`benchmark-run-${runKey}-results.csv`, text, 'text/csv;charset=utf-8');
  },

  /** Why one case got its tier: points, evidence per layer and agent, KYC, alerts. */
  getCaseDetail(runKey: number, caseRef: string) {
    return get<CaseDetailResponse>(`/runs/${runKey}/cases/${encodeURIComponent(caseRef)}`);
  },

  /** The alerts, and any KYC, a supplied-sample run was given. */
  getRunSample(runKey: number, params: { limit?: number; offset?: number } = {}) {
    return get<RunSampleResponse>(`/runs/${runKey}/sample`, params);
  },

  getExecutiveSummary(runKey: number) {
    return get<ExecutiveSummaryResponse>(`/runs/${runKey}/executive-summary`);
  },

  getWorkloadSimulator(runKey: number) {
    return get<WorkloadSimulatorResponse>(`/runs/${runKey}/workload-simulator`);
  },

  getDispositionMatrix(runKey: number) {
    return get<DispositionMatrixResponse>(`/runs/${runKey}/disposition-matrix`);
  },

  getTypologyPerformance(runKey: number) {
    return get<TypologyPerformanceResponse>(`/runs/${runKey}/typology-performance`);
  },

  getRiskDrivers(runKey: number) {
    return get<RiskDriversResponse>(`/runs/${runKey}/risk-drivers`);
  },

  getScoredAlerts(runKey: number, params: ScoredAlertsQuery = {}) {
    return get<ScoredAlertsResponse>(`/runs/${runKey}/alerts`, params);
  },

  getRegulatoryDossier(runKey: number) {
    return get<RegulatoryDossierResponse>(`/runs/${runKey}/regulatory-dossier`);
  },

  /** Every dimension an investigation run compared, with its headline metrics. */
  getDimensions(runKey: number, filters: { dimensionType?: DimensionType; producer?: string } = {}) {
    const params: Record<string, string> = {};
    if (filters.dimensionType) params.dimension_type = filters.dimensionType;
    if (filters.producer) params.producer = filters.producer;
    return get<DimensionsResponse>(`/runs/${runKey}/dimensions`, params);
  },

  /** Per dimension, each side's association with one outcome, ranked by the platform's gain. */
  getOutcomeAssociation(runKey: number, query: OutcomeAssociationQuery = {}) {
    const params: Record<string, string> = {};
    if (query.outcome) params.outcome = query.outcome;
    if (query.dimensionType) params.dimension_type = query.dimensionType;
    if (query.producer) params.producer = query.producer;
    if (query.cohort) params.cohort = query.cohort;
    return get<OutcomeAssociationResponse>(`/runs/${runKey}/outcome-association`, params);
  },

  /** The agents a flow can run and their risk factors: what the launch picker offers. */
  getAgentCatalog(flowCode: string) {
    return get<AgentCatalog>('/agent-catalog', { flow_code: flowCode });
  },

  /** One dimension's grid, metrics and not-comparable breakdown. Keys carry a colon, so encoded. */
  getDimension(runKey: number, dimensionKey: string) {
    return get<DimensionDetailResponse>(`/runs/${runKey}/dimensions/${encodeURIComponent(dimensionKey)}`);
  },

  /** The cases behind one dimension, filtered and paged. */
  getDimensionSubjects(runKey: number, dimensionKey: string, query: DimensionSubjectsQuery = {}) {
    return get<DimensionSubjectsResponse>(
      `/runs/${runKey}/dimensions/${encodeURIComponent(dimensionKey)}/subjects`, query);
  },

  /** Downloads every case matching the filters as CSV, built by the server. */
  async downloadDimensionSubjectsCsv(
    runKey: number, dimensionKey: string, query: Omit<DimensionSubjectsQuery, 'limit' | 'offset'> = {}
  ): Promise<void> {
    let text: string;
    try {
      const res = await axios.get(
        `${getBaseUrl()}/runs/${runKey}/dimensions/${encodeURIComponent(dimensionKey)}/subjects`, {
          headers: getHeaders(),
          params: { ...query, format: 'csv' },
          responseType: 'text'
        });
      text = String(res.data ?? '');
    } catch (error) {
      throw toRequestError(error);
    }
    const safeKey = dimensionKey.replace(/[^A-Za-z0-9_-]+/g, '-');
    downloadText(`benchmark-run-${runKey}-${safeKey}.csv`, text, 'text/csv;charset=utf-8');
  },

  /** Whether the caller's org can benchmark a supplied sample, and what is missing. */
  getSamplePrerequisites() {
    return get<SamplePrerequisites>('/sample-prerequisites');
  },

  /** How the org's supplied files are read, with unconfirmed model mappings listed. */
  getFieldMappings(relation: MappingRelation) {
    return get<FieldMappingsResponse>('/field-mappings', { relation });
  },

  /**
   * Confirm or reject columns the model identified. Changes how every later
   * file from the org is read; a column a person already confirmed is kept.
   */
  confirmFieldMappings(body: { relation: MappingRelation; columns: Record<string, string>; reject: string[] }) {
    return post<ConfirmMappingsResponse>('/field-mappings/confirm', body);
  },

  /**
   * Runs the workflow's supplied-sample flow over records the user provided.
   *
   * With `previewOnly` the loader reads and checks the sample and reports how
   * it was read, and nothing is replayed or written. Without it the sample is
   * benchmarked, and the run is found afterwards by the returned referenceId.
   */
  async submitSuppliedSample(agentCode: string, interactionId: string, data: SuppliedSampleData): Promise<SuppliedSampleSubmission> {
    try {
      const res = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        { agent: { code: agentCode }, user: { interactionId, projectId: data.projectId }, data },
        { headers: getHeaders() }
      );
      // This endpoint can report a failure inside a 2xx body.
      const body = res.data;
      const errorMessage = typeof body?.error === 'string' ? body.error.trim() : '';
      if (errorMessage || Number(body?.status) >= 400) {
        throw new BenchmarkRequestError(errorMessage || 'The sample could not be submitted.', Number(body?.status) || null);
      }
      const reply = body?.data ?? {};
      return {
        referenceId: reply.referenceId ?? null,
        interactionId: reply.interactionId ?? interactionId,
        async: Boolean(reply.async),
        status: reply.status ?? null
      };
    } catch (error) {
      if (error instanceof BenchmarkRequestError) throw error;
      const serverMessage = (error as any)?.response?.data?.error;
      if (serverMessage) throw new BenchmarkRequestError(serverMessage, (error as any)?.response?.status ?? null);
      throw toRequestError(error);
    }
  }
};
